import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonAxisSettings } from 'devextreme/viz/chart';
import { switchMap } from 'rxjs';
import { TrainingFeedback } from 'src/app/models/training/training-feedback.model';
import { LoadingService } from '../../../services/loading.service';
import { TrainingService } from '../../../services/training.service';
import { DEFAULT_SCALE_END, DEFAULT_SCALE_START } from 'src/app/constants/feedback.constants';

interface FeedbackQuestionData {
  question: string;
  averageScore?: number;
  chartData?: { id: number; value: number }[];
  distribution?: { [star: number]: number };
  responseCount?: number;
  answers?: string[];
  maxChartData?: number;
  scaleStart?: number;
  scaleEnd?: number;
}

interface FeedbackPageData {
  title: string;
  questions: FeedbackQuestionData[];
  averageScore?: number;
  questionCount?: number;
  responseCount?: number;
}

@Component({
  selector: 'app-training-feedback',
  templateUrl: './training-feedback.component.html',
  styleUrl: './training-feedback.component.scss',
  standalone: false
})
export class TrainingFeedbackComponent implements OnInit {
  trainingId!: string;
  feedback: TrainingFeedback | null = null;

  commonAxisSettings: CommonAxisSettings = {
    color: '#dddddd',
    grid: { color: '#dddddd' },
    tick: { visible: true, color: '#dddddd' },
    aggregatedPointsPosition: 'crossTicks',
    label: {
      font: {
        color: '#000',
        size: 10,
      },
      visible: true
    },
  };

  pagesData: FeedbackPageData[] = [];

  overallRating = 0;
  defaultRatings: number[] = 
    this.buildRange(DEFAULT_SCALE_START, DEFAULT_SCALE_END);
  maxScore = DEFAULT_SCALE_END;

  constructor(
    private activatedRoute: ActivatedRoute,
    private loadingService: LoadingService,
    private trainingService: TrainingService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        switchMap(param => {
          const id = param['id'];
          this.trainingId = id;

          return this.loadingService.load(
            this.trainingService.getTrainingFeedback(id),
          );
        }),
      )
      .subscribe(feedback => {
        this.feedback = feedback;

        this.pagesData = feedback.pages.map(page => ({
          title: page.title,
          questions: page.questions.map(question => {
            const questionData: FeedbackQuestionData = {} as any;

            if (question.type === 1) {
              questionData.question = question.text;
              questionData.scaleStart = question.scaleStart;
              questionData.scaleEnd = question.scaleEnd;

              questionData.chartData = new Array(
                question.scaleEnd - question.scaleStart + 1,
              )
                .fill(1)
                .map((_, index) => {
                  const value = question.scaleStart + index;
                  return {
                    id: value,
                    value: question.answers.reduce((acc, answer) => {
                      if (answer.value === value) {
                        acc++;
                      }
                      return acc;
                    }, 0),
                  };
                });

              questionData.averageScore =
                question.answers.reduce((acc: number, item) => {
                  acc += item.value;
                  return acc;
                }, 0) / question.answers.length;

              questionData.maxChartData = Math.max(
                ...questionData.chartData.map(item => item.value),
              );

              questionData.distribution = questionData.chartData.reduce(
                (acc: { [star: number]: number }, item) => {
                  acc[item.id] = item.value;
                  return acc;
                },
                {},
              );

              questionData.responseCount = question.answers.length;
            }

            if (question.type === 0) {
              questionData.question = question.text;
              questionData.answers = question.answers.map(
                answer => answer.text,
              );
            }

            return questionData;
          }),
        }));

        this.pagesData = this.pagesData.map(page => {
          const scoredQuestions = page.questions.filter(q => q.averageScore);
          return {
            ...page,
            questionCount: page.questions.length,
            responseCount: scoredQuestions[0]?.responseCount ?? 0,
            averageScore:
              scoredQuestions.reduce((acc: number, item) => {
                acc += item.averageScore || 0;
                return acc;
              }, 0) / (scoredQuestions.length || 1),
          };
        });

        const scoredPages = this.pagesData.filter(p => p.averageScore);
        this.overallRating =
          scoredPages.reduce((acc, p) => acc + (p.averageScore || 0), 0) /
          (scoredPages.length || 1);
      });
  }

  get highestRatedPage(): FeedbackPageData | null {
    return this.pagesData
      .filter(p => p.averageScore)
      .reduce(
        (max: FeedbackPageData | null, p) =>
          !max || (p.averageScore || 0) > (max.averageScore || 0) ? p : max,
        null,
      );
  }

  get lowestRatedPage(): FeedbackPageData | null {
    return this.pagesData
      .filter(p => p.averageScore)
      .reduce(
        (min: FeedbackPageData | null, p) =>
          !min || (p.averageScore || 0) < (min.averageScore || 0) ? p : min,
        null,
      );
  }

  getRatingsRange(question: { scaleStart?: number; scaleEnd?: number }): number[] {
    const start = question.scaleStart ?? DEFAULT_SCALE_START;
    const end = question.scaleEnd ?? DEFAULT_SCALE_END;
    return this.buildRange(start, end);
  }

  getRatingsRangeDesc(question: { scaleStart?: number; scaleEnd?: number }): number[] {
    return this.getRatingsRange(question).slice().reverse();
  }

  getDistributionWidth(question: FeedbackQuestionData, star: number): number {
    if (!question.distribution) return 0;
    const max = Math.max(...Object.values(question.distribution));
    if (!max) return 0;
    return ((question.distribution[star] || 0) / max) * 90;
  }

  getDistributionOpacity(question: { scaleStart?: number; scaleEnd?: number }, star: number): number {
    const start = question.scaleStart ?? DEFAULT_SCALE_START;
    const end = question.scaleEnd ?? DEFAULT_SCALE_END;
    const range = end - start || 1;
    const position = (star - start) / range;
    return 0.3 + position * 0.7;
  }

  navigateBack() {
    this.router.navigate(['training', this.trainingId]);
  }

  print(): void {
    window.print();
  }

  private buildRange(start: number, end: number): number[] {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  }
}